import { memo, useState, useEffect } from "react";
import { Drawer, Box, Toolbar } from "@mui/material";
import toast from "react-hot-toast";
import { v4 as uuidv4 } from "uuid";
import ConditionalsList from "./ConditionalsList";
import AddConditional from "./AddConditional";

const Conditional = ({ conditionalDrawerOpen, handleCloseConditional }) => {
  const [conditionals, setConditionals] = useState([]);
  const [showAddScreen, setShowAddScreen] = useState(false);
  const [editingConditional, setEditingConditional] = useState(null);
  const CONDITIONALS_STORAGE_KEY = "conditionals_presets";

  // Load conditionals from localStorage on mount
  useEffect(() => {
    try {
      const savedConditionals = localStorage.getItem(CONDITIONALS_STORAGE_KEY);
      if (savedConditionals) {
        const parsed = JSON.parse(savedConditionals);
        if (Array.isArray(parsed)) {
          setConditionals(parsed);
        } else {
          console.warn("Invalid conditionals data structure, resetting to empty");
          localStorage.removeItem(CONDITIONALS_STORAGE_KEY);
        }
      }
    } catch (error) {
      console.error("Failed to load conditionals from localStorage:", error);
      toast.error("Failed to load saved conditionals");
      localStorage.removeItem(CONDITIONALS_STORAGE_KEY);
    }
  }, []);

  // Save conditionals to localStorage whenever they change
  useEffect(() => {
    try {
      if (conditionals.length > 0) {
        const jsonString = JSON.stringify(conditionals);
        localStorage.setItem(CONDITIONALS_STORAGE_KEY, jsonString);
      } else {
        localStorage.removeItem(CONDITIONALS_STORAGE_KEY);
      }
    } catch (error) {
      console.error("Failed to save conditionals to localStorage:", error);
      // Check if it's a quota exceeded error
      if (error.name === "QuotaExceededError") {
        toast.error("Storage limit exceeded. Please remove some conditionals.");
      } else {
        toast.error("Failed to save conditionals");
      }
    }
  }, [conditionals]);

  const onClose = () => {
    setShowAddScreen(false);
    setEditingConditional(null);
    handleCloseConditional();
  };

  const handleAddConditional = () => {
    setEditingConditional(null);
    setShowAddScreen(true);
  };

  const handleBackOrDiscard = () => {
    if (conditionals.length === 0) {
      onClose();
    } else {
      setShowAddScreen(false);
      setEditingConditional(null);
    }
  };

  const handleViewConditional = (id) => {
    const conditional = conditionals.find((c) => c.id === id);
    if (conditional) {
      setEditingConditional(conditional);
      setShowAddScreen(true);
    }
  };

  const onSave = (conditionalData) => {
    if (editingConditional) {
      setConditionals((prev) =>
        prev.map((c) =>
          c.id === editingConditional.id
            ? {
                ...c,
                ...conditionalData,
              }
            : c
        )
      );
    } else {
      const newConditional = {
        id: uuidv4(),
        ...conditionalData,
      };
      setConditionals((prev) => [...prev, newConditional]);
    }

    setShowAddScreen(false);
    setEditingConditional(null);
  };

  const onDelete = (id) => {
    setConditionals((prev) => prev.filter((c) => c.id !== id));
    setShowAddScreen(false);
    setEditingConditional(null);
    toast.success("Column rule removed successfully");
  };

  return (
    <Drawer
      anchor="right"
      open={conditionalDrawerOpen}
      onClose={onClose}
      variant="persistent"
      slotProps={{
        paper: {
          sx: { width: { xs: "100%", sm: 400 }, display: "flex", flexDirection: "column" },
        },
      }}
    >
      <Toolbar />
      <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        {!showAddScreen && conditionals.length > 0 ? (
          <ConditionalsList
            {...{
              conditionals,
              onClose,
              handleAddConditional,
              handleViewConditional,
            }}
          />
        ) : (
          <AddConditional
            {...{
              handleBackOrDiscard,
              onClose,
              conditionals,
              editingConditional,
              onSave,
              onDelete,
            }}
          />
        )}
      </Box>
    </Drawer>
  );
};

export default memo(Conditional);
